import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import ResizeObserverPolyfill from "resize-observer-polyfill";

// Drop-in replacement for `react-resize-aware` with the same API:
//   const [resizeListener, sizes] = useResizeAware();
// Render `resizeListener` inside a `position: relative` box and `sizes`
// reports that box's width/height.
//
// Why not react-resize-aware: it measures through a hidden
// `<iframe src="about:blank">`. Locked-down WebViews (e.g. MDM browsers on
// school tablets) block about:blank frames / contentDocument access, so the
// size never resolves and every template that self-measures for the portrait
// stage silently falls back to the landscape layout. ResizeObserver needs no
// iframe and works in the same WebViews. Also avoids the broken 3.1.3 build of
// react-resize-aware (bare `jsx` reference → "jsx is not defined").

const RO =
  typeof window !== "undefined" && window.ResizeObserver
    ? window.ResizeObserver
    : ResizeObserverPolyfill;

const listenerStyle = {
  display: "block",
  position: "absolute",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  pointerEvents: "none",
  visibility: "hidden",
  zIndex: -1,
  maxHeight: "inherit",
  maxWidth: "inherit"
};

const measure = (el) => {
  if (!el) return { width: null, height: null };
  const width = el.offsetWidth;
  const height = el.offsetHeight;
  return { width, height };
};

const ResizeListener = ({ onResize }) => {
  const ref = useRef(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    onResize(measure(el));

    const observer = new RO(() => {
      onResize(measure(el));
    });
    observer.observe(el);

    return () => observer.disconnect();
  }, [onResize]);

  return <span ref={ref} style={listenerStyle} aria-hidden />;
};

export default function useResizeAware() {
  const [sizes, setSizes] = useState({ width: null, height: null });

  const onResize = useCallback((next) => {
    setSizes((prev) =>
      prev.width === next.width && prev.height === next.height ? prev : next
    );
  }, []);

  const resizeListener = useMemo(
    () => <ResizeListener onResize={onResize} />,
    [onResize]
  );

  return [resizeListener, sizes];
}
